// ===========================================
// CreditNova Financial Overrides Page (Concurrent Workflow)
// ===========================================
//
// Route: /workflow/overrides
// Purpose: Tabbed override view for multiple financial statement extractions.
//          Each tab renders an ExtractionResults component for one statement,
//          allowing the user to review and correct extracted values before
//          cross-period analysis and report generation.
//
// Flow:
//   upload.tsx detects FS worker jobs complete → extracts FS data →
//   stores in WorkflowContext.financialExtractions → navigates here.
//
//   User reviews/overrides each statement tab → clicks "Confirm & Continue"
//   → overridden data stored in WorkflowContext.overriddenFinancialExtractions
//   → navigates to /workflow/metrics.
//
// ===========================================

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, FileText, Lock, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api-client';
import { ExtractionResults, type Override } from '@/components/extraction';
import type { FinancialData } from '@creditnova/shared/schemas';

// Sort extractions so latest fiscal year is first
function extractYear(periodStr: string): number {
  const match = String(periodStr).match(/\b(19|20)\d{2}\b/);
  return match ? parseInt(match[0], 10) : 0;
}
function sortByLatestPeriod<T extends { data: any; period?: string }>(extractions: T[]): T[] {
  if (extractions.length <= 1) return extractions;
  return [...extractions].sort((a, b) => {
    const yearA = extractYear(a.data?.periodEnding || a.period || '');
    const yearB = extractYear(b.data?.periodEnding || b.period || '');
    return yearB - yearA;
  });
}

// ===========================================
// Route Registration
// ===========================================

export const Route = createFileRoute('/workflow/_layout/overrides')({
  component: OverridesStep,
});

// ===========================================
// OverridesStep Component
// ===========================================

function OverridesStep() {
  const { setCurrentStep, updateData, data, goToStep } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------

  // Track unlocked count PER TAB — user can't continue until all tabs have 0 unlocked
  const [unlockedCountByTab, setUnlockedCountByTab] = useState<Record<string, number>>({});

  // Track overrides PER TAB
  const [overridesByTab, setOverridesByTab] = useState<Record<string, Override[]>>({});

  // Active tab
  const [activeTab, setActiveTab] = useState<string>('');

  // Base financial data refs — one per tab (keyed by documentId)
  // Stores the original extraction before overrides are applied
  const baseDataRef = useRef<Record<string, FinancialData>>({});

  // Override persistence refs
  const savedOverrideKeysRef = useRef<Set<string>>(new Set());
  const pendingOverridesRef = useRef<Record<string, Override[]>>({});
  pendingOverridesRef.current = overridesByTab;

  // ---------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------

  // Report is in-flight or done — page is view-only to prevent misleading edits
  const isReadonly = data.workflowState === 'generating' || data.workflowState === 'complete';

  const financialExtractions = data.financialExtractions;

  // Initialize active tab
  useEffect(() => {
    if (financialExtractions.length > 0 && !activeTab) {
      setActiveTab(financialExtractions[0].documentId);
    }
  }, [financialExtractions, activeTab]);

  // Initialize base data refs
  useEffect(() => {
    for (const extraction of financialExtractions) {
      if (!baseDataRef.current[extraction.documentId]) {
        baseDataRef.current[extraction.documentId] = JSON.parse(JSON.stringify(extraction.data));
      }
    }
  }, [financialExtractions]);

  // Total unlocked across all tabs
  const totalUnlocked = useMemo(
    () => Object.values(unlockedCountByTab).reduce((sum, count) => sum + count, 0),
    [unlockedCountByTab],
  );

  // Per-tab completion status (all rows locked)
  const tabCompletion = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const ext of financialExtractions) {
      // A tab is complete when its unlocked count is 0
      // (undefined means ExtractionResults hasn't reported yet — treat as incomplete)
      result[ext.documentId] = (unlockedCountByTab[ext.documentId] ?? -1) === 0;
    }
    return result;
  }, [financialExtractions, unlockedCountByTab]);

  // ---------------------------------------------------------------
  // Sync workflow step
  // ---------------------------------------------------------------

  useEffect(() => {
    setCurrentStep('overrides');
  }, [setCurrentStep]);

  // If no financial extractions exist, redirect back to upload
  useEffect(() => {
    if (financialExtractions.length === 0) {
      navigate({ to: '/workflow/upload', search: { workflowId: undefined } });
    }
  }, [financialExtractions, navigate]);

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------

  // Called by ExtractionResults when overrides change for a specific tab
  const handleOverridesChange = useCallback((documentId: string, overrides: Override[]) => {
    setOverridesByTab((prev) => ({ ...prev, [documentId]: overrides }));

    // Apply overrides to the extraction data
    const base = baseDataRef.current[documentId];
    if (!base) return;

    const updatedData: FinancialData = JSON.parse(JSON.stringify(base));
    // Strip pre-extracted standardizedFields when overrides are applied.
    // The overridden line-item values may no longer match the cached fields,
    // so downstream ratio calculation must re-derive them from the new data.
    if (overrides.length > 0) {
      delete (updatedData as any).standardizedFields;
    }
    for (const override of overrides) {
      const [section, ...rest] = override.fieldPath.split('.');
      const key = rest.join('.');
      const dataRecord = updatedData.data as Record<string, Record<string, string> | null>;
      if (section && key && dataRecord[section]) {
        dataRecord[section]![key] = override.overrideValue;
      }
    }

    // Update the specific extraction in context
    const updatedExtractions = data.financialExtractions.map((ext) =>
      ext.documentId === documentId ? { ...ext, data: updatedData } : ext
    );
    updateData({ financialExtractions: updatedExtractions });
  }, [data.financialExtractions, updateData]);

  // Called by ExtractionResults when unlocked count changes for a tab
  const handleUnlockedCountChange = useCallback((documentId: string, count: number) => {
    setUnlockedCountByTab((prev) => ({ ...prev, [documentId]: count }));
  }, []);

  // Persist overrides to backend
  const flushOverrides = useCallback(async (allOverrides: Record<string, Override[]>) => {
    const unsaved: Array<Override & { documentId: string }> = [];
    for (const [documentId, overrides] of Object.entries(allOverrides)) {
      for (const o of overrides) {
        const key = `${documentId}:${o.fieldPath}`;
        if (!savedOverrideKeysRef.current.has(key)) {
          unsaved.push({ ...o, documentId });
        }
      }
    }
    if (unsaved.length === 0) return;

    // Mark keys as saved BEFORE the API call to prevent duplicate
    // submissions from concurrent flushes (e.g., unmount + confirm)
    for (const o of unsaved) {
      savedOverrideKeysRef.current.add(`${o.documentId}:${o.fieldPath}`);
    }

    try {
      await Promise.all(
        unsaved.map((override) =>
          api.overrides.create({
            workflowId: data.workflowId,
            source: 'ui',
            fieldPath: override.fieldPath,
            fieldLabel: override.fieldLabel,
            originalValue: override.originalValue,
            overrideValue: override.overrideValue,
            justification: 'Adjusted by analyst',
          })
        )
      );
    } catch (error) {
      // Roll back the optimistic marks so a retry can succeed
      for (const o of unsaved) {
        savedOverrideKeysRef.current.delete(`${o.documentId}:${o.fieldPath}`);
      }
      console.warn('Failed to persist overrides to API:', error);
    }
  }, [data.workflowId]);

  // Debounced save
  useEffect(() => {
    const hasAny = Object.values(overridesByTab).some((arr) => arr.length > 0);
    if (!hasAny) return;

    const timer = setTimeout(() => flushOverrides(overridesByTab), 2000);
    return () => clearTimeout(timer);
  }, [overridesByTab, flushOverrides]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      const current = pendingOverridesRef.current;
      const hasAny = Object.values(current).some((arr) => arr.length > 0);
      if (hasAny) {
        flushOverrides(current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Confirm & Continue — store overridden data and navigate to metrics
  const handleConfirmAndContinue = useCallback(async () => {
    // Flush any pending overrides immediately
    flushOverrides(overridesByTab);

    // Store the overridden financial extractions and advance workflow state
    const overriddenExtractions = data.financialExtractions.map((ext) => ({
      documentId: ext.documentId,
      period: ext.period,
      fileName: ext.fileName,
      data: ext.data, // Already has overrides applied via handleOverridesChange
    }));

    updateData({
      overriddenFinancialExtractions: overriddenExtractions,
      // Also update the primary financialData — prefer latest fiscal year
      financialData: sortByLatestPeriod(data.financialExtractions)[0]?.data || null,
      // Advance state so resume lands on metrics, not overrides
      workflowState: 'metrics_review',
    });

    // Await the backend PATCH so the state is persisted before the user
    // can refresh. This ensures resume always lands on the correct step.
    if (data.workflowId) {
      try {
        await api.workflows.update(data.workflowId, {
          overriddenFinancialExtractions: overriddenExtractions,
          workflowState: 'metrics_review',
        });
      } catch (err) {
        console.warn('Failed to sync overriddenFinancialExtractions to backend:', err);
      }
    }

    toast({
      title: 'Overrides Confirmed',
      description: `Financial data reviewed for ${financialExtractions.length} statement${financialExtractions.length !== 1 ? 's' : ''}.`,
    });

    // Navigate to metrics page
    goToStep('metrics');
  }, [data.financialExtractions, data.workflowId, financialExtractions.length, flushOverrides, goToStep, overridesByTab, toast, updateData]);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  if (financialExtractions.length === 0) {
    return null; // Redirect effect will fire
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <FileText className="w-10 h-10 text-accent-500" />
          <h1 className="text-4xl font-bold">Review Financial Data</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Review and adjust extracted financial data for each statement.
          Lock all values to confirm accuracy before continuing.
        </p>
      </div>

      {/* Readonly banner — shown when report generation is in progress */}
      {isReadonly && (
        <Card className="p-4 rounded-2xl mb-6 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Report generation is {data.workflowState === 'complete' ? 'complete' : 'in progress'} — financial data is locked and view-only. Changes made here will not affect the report.
            </p>
          </div>
        </Card>
      )}

      {/* Single Statement — No Tabs Needed */}
      {financialExtractions.length === 1 && (
        <div className="space-y-6">
          <ExtractionResults
            data={financialExtractions[0].data}
            originalData={baseDataRef.current[financialExtractions[0].documentId]}
            onOverridesChange={(overrides) =>
              handleOverridesChange(financialExtractions[0].documentId, overrides)
            }
            onUnlockedCountChange={(count) =>
              handleUnlockedCountChange(financialExtractions[0].documentId, count)
            }
            readonly={isReadonly}
          />
        </div>
      )}

      {/* Multiple Statements — Tabbed Layout */}
      {financialExtractions.length > 1 && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 w-full justify-start">
            {financialExtractions.map((ext) => (
              <TabsTrigger
                key={ext.documentId}
                value={ext.documentId}
                className="flex items-center gap-2"
              >
                {tabCompletion[ext.documentId] && (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                )}
                <span>{ext.period || ext.fileName}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {financialExtractions.map((ext) => (
            <TabsContent key={ext.documentId} value={ext.documentId}>
              <ExtractionResults
                data={ext.data}
                originalData={baseDataRef.current[ext.documentId]}
                onOverridesChange={(overrides) =>
                  handleOverridesChange(ext.documentId, overrides)
                }
                onUnlockedCountChange={(count) =>
                  handleUnlockedCountChange(ext.documentId, count)
                }
                readonly={isReadonly}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Confirm & Continue — hidden when readonly (generating/complete) to prevent state regression */}
      {!isReadonly && (
        <Card className="p-6 rounded-2xl mt-6">
          <div className="flex items-center justify-between">
            <div>
              {totalUnlocked > 0 ? (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {totalUnlocked} unlocked value{totalUnlocked !== 1 ? 's' : ''} remaining —
                  lock all values to continue.
                </p>
              ) : (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  All values locked and confirmed.
                </p>
              )}
            </div>
            <Button
              size="lg"
              onClick={handleConfirmAndContinue}
              disabled={totalUnlocked > 0}
              className="min-w-[200px]"
            >
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Confirm & Continue
              </span>
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
